import { Injectable } from '@angular/core';
import { InvestigationDashboardPreferencesEntity } from '../../domain/entities/investigation-dashboard-preferences.entity';
import {
  InvestigationDashboardWidgetDataEntity,
  InvestigationDashboardWidgetDataPointEntity,
  InvestigationDashboardWidgetDataType,
} from '../../domain/entities/investigation-dashboard-widget-data.entity';
import { InvestigationDashboardWidgetEntity } from '../../domain/entities/investigation-dashboard-widget.entity';
import { DashboardInvestigationRowEntity } from '../../domain/entities/dashboard-investigation-row.entity';
import { InvestigationStatusSnapshotEntity } from '../../domain/entities/investigation-status-snapshot.entity';
import { InvestigationPrioritySnapshotEntity } from '../../domain/entities/investigation-priority-snapshot.entity';
import { InvestigationDashboardPreferencesResponseDto } from '../dtos/investigation-dashboard-preferences-response.dto';
import { InvestigationDashboardWidgetDataResponseDto } from '../dtos/investigation-dashboard-widget-data-response.dto';
import { InvestigationDashboardWidgetResponseDto } from '../dtos/investigation-dashboard-widget-response.dto';
import { DashboardInvestigationRowResponseDto } from '../dtos/dashboard-investigation-row-response.dto';
import { InvestigationStatusSnapshotResponseDto } from '../dtos/investigation-status-snapshot-response.dto';
import { InvestigationPrioritySnapshotResponseDto } from '../dtos/investigation-priority-snapshot-response.dto';

@Injectable({ providedIn: 'root' })
export class InvestigationDashboardMapper {
  public toWidget(dto: InvestigationDashboardWidgetResponseDto): InvestigationDashboardWidgetEntity {
    const entity = new InvestigationDashboardWidgetEntity();
    entity.id = dto.id;
    entity.dashboardViewTypeId = dto.dashboardViewTypeId;
    entity.widgetKey = dto.widgetKey;
    entity.displayName = dto.displayName;
    entity.displayOrder = dto.displayOrder;
    entity.widgetType = dto.widgetType;
    entity.isVisible = dto.isVisible;
    return entity;
  }

  public toWidgetArray(dtos: InvestigationDashboardWidgetResponseDto[]): InvestigationDashboardWidgetEntity[] {
    return dtos.map((dto) => this.toWidget(dto));
  }

  public toPreferences(dto: InvestigationDashboardPreferencesResponseDto): InvestigationDashboardPreferencesEntity {
    const entity = new InvestigationDashboardPreferencesEntity();
    entity.hiddenPredefinedFilterIds = dto.hiddenPredefinedFilterIds ?? [];
    entity.hiddenColumnKeys = dto.hiddenColumnKeys ?? [];
    entity.columnOrder = dto.columnOrder;
    entity.advancedFilterRows = dto.advancedFilterRows;
    entity.showCharts = dto.showCharts;
    entity.selectedTimeframe = dto.selectedTimeframe;
    entity.filterFromDate = dto.filterFromDate ?? null;
    entity.filterToDate = dto.filterToDate ?? null;
    entity.canCreateInvestigation = dto.canCreateInvestigation;
    return entity;
  }

  public toWidgetData(dto: InvestigationDashboardWidgetDataResponseDto): InvestigationDashboardWidgetDataEntity {
    const entity = new InvestigationDashboardWidgetDataEntity();
    entity.widgetKey = dto.widgetKey;
    entity.type = dto.type as InvestigationDashboardWidgetDataType;
    entity.data = dto.data.map((p) => {
      const point = new InvestigationDashboardWidgetDataPointEntity();
      point.key = p.key;
      point.label = p.label;
      point.value = p.value;
      point.color = p.color;
      point.filter = p.filter;
      return point;
    });
    return entity;
  }

  public toDashboardRowDomain(dto: DashboardInvestigationRowResponseDto): DashboardInvestigationRowEntity {
    const entity = new DashboardInvestigationRowEntity();
    entity.id = dto.id;
    entity.investigationNumber = dto.investigationNumber;
    entity.investigationTypeName = dto.investigationTypeName;
    entity.investigationTypeCategoryName = dto.investigationTypeCategoryName;
    entity.requestorName = dto.requestorName;
    entity.statusName = dto.statusName;
    entity.statusDisplayName = dto.statusDisplayName;
    entity.statusColor = dto.statusColor;
    entity.statusCategoryId = dto.statusCategoryId;
    entity.statusCategoryName = dto.statusCategoryName ?? null;
    entity.isReadOnly = dto.isReadOnly;
    entity.priorityCode = dto.priorityCode;
    entity.primaryAssigneeUserId = dto.primaryAssigneeUserId;
    entity.primaryAssigneeName = dto.primaryAssigneeName;
    entity.secondaryAssigneeUserId = dto.secondaryAssigneeUserId;
    entity.secondaryAssigneeName = dto.secondaryAssigneeName;
    entity.assignedTeamId = dto.assignedTeamId;
    entity.assignedTeamName = dto.assignedTeamName;
    entity.requestorEmail = dto.requestorEmail;
    entity.requestorDepartment = dto.requestorDepartment;
    entity.requestorDivision = dto.requestorBusinessUnit;
    entity.createdByUserId = dto.createdByUserId;
    entity.createdByName = dto.createdByName;
    entity.updatedByUserId = dto.updatedByUserId;
    entity.updatedByName = dto.updatedByName;
    entity.createdOn = dto.createdOn;
    entity.updatedOn = dto.updatedOn;
    entity.dueDate = dto.dueDate;
    entity.slaStatus = dto.slaStatus;
    entity.unreadMessageCount = dto.unreadMessageCount;
    entity.hasMessages = dto.hasMessages;
    entity.isCurrentUserWatching = dto.isCurrentUserWatching;
    entity.requestedByUserId = dto.requestedByUserId;
    entity.excludedAssigneeUserIds = dto.excludedAssigneeUserIds ?? [];
    return entity;
  }

  public toDashboardRowsDomain(dtos: DashboardInvestigationRowResponseDto[]): DashboardInvestigationRowEntity[] {
    return dtos.map((dto) => this.toDashboardRowDomain(dto));
  }

  public toStatusSnapshot(dto: InvestigationStatusSnapshotResponseDto): InvestigationStatusSnapshotEntity {
    const entity = new InvestigationStatusSnapshotEntity();
    entity.id = dto.id;
    entity.name = dto.name;
    entity.displayName = dto.displayName;
    entity.color = dto.color;
    entity.categoryId = dto.categoryId;
    entity.sortOrder = dto.sortOrder;
    entity.isActive = dto.isActive;
    return entity;
  }

  public toStatusSnapshotArray(dtos: InvestigationStatusSnapshotResponseDto[]): InvestigationStatusSnapshotEntity[] {
    return dtos.map((dto) => this.toStatusSnapshot(dto));
  }

  public toPrioritySnapshot(dto: InvestigationPrioritySnapshotResponseDto): InvestigationPrioritySnapshotEntity {
    const entity = new InvestigationPrioritySnapshotEntity();
    entity.id = dto.id;
    entity.priority = dto.priority;
    entity.name = dto.name;
    entity.code = dto.priority;
    entity.sortOrder = dto.sortOrder;
    entity.timeToResolveHours = dto.timeToResolveHours;
    entity.atRiskThresholdPercent = dto.atRiskThresholdPercent;
    entity.isActive = dto.isActive;
    return entity;
  }

  public toPrioritySnapshotArray(dtos: InvestigationPrioritySnapshotResponseDto[]): InvestigationPrioritySnapshotEntity[] {
    return dtos.map((dto) => this.toPrioritySnapshot(dto));
  }
}
